import { useEffect, useState } from "react";
import {
  NO_MEMBERSHIP,
  PortalMembership,
  portalScopeService,
} from "@/services/portalScope.service";
import { usePortalScope } from "./hooks";

export interface MembershipState {
  membership: PortalMembership;
  /** True until the answer is known. Callers must not redirect while true. */
  isLoading: boolean;
}

/**
 * Whether a scoped affiliate has joined their home brand yet.
 *
 * Only fetches when the affiliate is actually scoped — an unscoped affiliate
 * settles immediately to "no membership" without a request, so nothing about
 * the marketplace portal changes, including its network traffic.
 *
 * Never cached. The answer changes the moment someone joins, and a stale
 * "not joined" would bounce them back to the join page they just came from.
 */
export function usePortalMembership(): MembershipState {
  const { scope, isLoading: scopeLoading } = usePortalScope();
  const [state, setState] = useState<MembershipState>({
    membership: NO_MEMBERSHIP,
    isLoading: true,
  });

  useEffect(() => {
    if (scopeLoading) return;
    if (!scope.isScoped) {
      setState({ membership: NO_MEMBERSHIP, isLoading: false });
      return;
    }

    let cancelled = false;
    portalScopeService
      .getMyMembership()
      .then((membership) => {
        if (!cancelled) setState({ membership, isLoading: false });
      })
      .catch(() => {
        // Fail towards letting them in. A lookup failure is not evidence that
        // they have not joined, and locking a genuine member out of their own
        // portal is worse than showing the dashboard to someone who has not.
        if (!cancelled) {
          setState({
            membership: { ...NO_MEMBERSHIP, isScoped: true, hasMembership: true },
            isLoading: false,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scope.isScoped, scopeLoading]);

  return state;
}
